import { encodeDeepLink } from "@tg-gallery/deep-links"
import { Camera, Plus, UserPlus } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useNavigate } from "react-router"
import { GalleryList } from "../components/GalleryList.js"
import { InboxSection } from "../components/InboxSection.js"
import { getTelegramWebApp, isTelegramMiniApp } from "../lib/telegram.js"
import { useStore, useT } from "../stores/StoreProvider.js"
import { LandingPage } from "./LandingPage.js"

export const HomePage = observer(function HomePage() {
  const { authStore, galleryStore } = useStore()
  const navigate = useNavigate()
  const t = useT()

  if (!isTelegramMiniApp() && !authStore.isAuthenticated) {
    return <LandingPage />
  }

  const handleCreate = async () => {
    const title = `Gallery ${new Date().toLocaleDateString()}`
    const gallery = await (galleryStore.createGallery(title) as Promise<{ id: string } | null>)
    if (gallery) {
      navigate(`/gallery/${gallery.id}/settings`)
    }
  }

  const handleInvite = () => {
    const userId = authStore.userId
    if (!userId) return
    const botUsername = (import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? "").replace("@", "")
    const startParam = encodeDeepLink({ type: "referral", userId })
    const inviteLink = `https://t.me/${botUsername}?start=${startParam}`
    const webapp = getTelegramWebApp()
    if (webapp) {
      webapp.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(t("home.invite_friends"))}`
      )
    } else {
      void navigator.clipboard.writeText(inviteLink)
    }
  }

  return (
    <div className="min-h-screen bg-tg-bg">
      <header
        className="sticky top-0 z-10 backdrop-blur-xl border-b border-tg-secondary/40 px-4 py-3"
        style={{
          background: "linear-gradient(135deg, var(--gallery-bg) 0%, color-mix(in srgb, var(--gallery-accent) 6%, var(--gallery-bg)) 100%)",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, var(--gallery-accent), color-mix(in srgb, var(--gallery-accent) 80%, #6366f1))",
              }}
            >
              <Camera className="w-[18px] h-[18px] text-white" />
            </div>
            <h1 className="text-lg font-bold text-tg-text tracking-tight">{t("home.title")}</h1>
          </div>
          <div className="flex items-center gap-2">
            {authStore.isAuthenticated && isTelegramMiniApp() && (
              <button
                type="button"
                onClick={handleInvite}
                className="w-8 h-8 rounded-full bg-tg-secondary flex items-center justify-center btn-press"
              >
                <UserPlus className="w-4 h-4 text-tg-hint" />
              </button>
            )}
            {(authStore.isAuthenticated || isTelegramMiniApp()) && (
              <button
                type="button"
                onClick={() => void handleCreate()}
                className="w-8 h-8 rounded-full bg-tg-accent flex items-center justify-center btn-press"
              >
                <Plus className="w-5 h-5 text-tg-button-text" />
              </button>
            )}
          </div>
        </div>
      </header>
      <InboxSection />
      <GalleryList />
    </div>
  )
})
